// @flow

import { useMemo, useState } from 'react';

import { CornerDownRight } from 'lucide-react';
import { useDispatch } from 'react-redux';

import { selectOneProductErrors, type DecoratedProduct } from '@gandi/cart';
import { updateCartProduct, getCart } from '@gandi/cart';
import { useTranslator } from '@gandi/translate';
import { Fqdn, Text } from '@gandi/ui';

import type { GandiFormSelectOption } from 'src/Domain/types';
import type { IDNLangAlpha2Code } from 'src/DomainCreate/types';
import { useNotifications } from 'src/hooks';
import { useAppSelector } from 'src/hooks/useAppSelector';

import { ListRow } from '../../ListRow';

import { StudioPlan } from './StudioPlan';
import ProductItem from './components/ProductItem';

import { AlphabetRow } from './components/Rows';

type Props = {
  product: DecoratedProduct,
};

export function StudioConvert({ product }: Props): React.Node {
  const dispatch = useDispatch();
  const [isPatchingLang, setIsPatchingLang] = useState(false);

  const { __ } = useTranslator();
  const { notifyMaybeApiError } = useNotifications();

  // errors
  const productErrors = useAppSelector(state =>
    selectOneProductErrors(state, {
      productId: product.id,
    }),
  );

  const { filteredProductErrors } = useMemo(() => {
    const filtered = productErrors
      // solved by the contact wizard
      .filter(error => !/(owner|admin|bill|tech).*/.test(error.name))
      // solved by alphabet row
      .filter(err => err.name !== 'lang')
      // extra-params errors require_x-uk_contact_type
      .filter(error => !/\.x-/.test(error.name));

    return {
      filteredProductErrors: filtered,
    };
  }, [productErrors]);

  const langError = productErrors.some(err => err.name === 'lang');

  // domain lang / alphabet
  const handleLangChange = async (
    selectedOption: GandiFormSelectOption<IDNLangAlpha2Code>,
  ): Promise<void> => {
    setIsPatchingLang(true);

    try {
      await dispatch(
        updateCartProduct({
          productId: product.id,
          payload: {
            ...product.payload,
            // $FlowFixMe[prop-missing] flow thinks that domain.fqdn is missing
            domain: {
              ...product.payload.domain,
              lang: selectedOption.value,
            },
          },
        }),
      );

      await dispatch(getCart());
    } catch (e) {
      notifyMaybeApiError(e);
    }

    setIsPatchingLang(false);
  };

  const label = __('My website with Gandi Studio');

  const price =
    product.isWorking ||
    // product.total?.before_taxes === 0 when there is a lang error
    (productErrors.length > 0 && product.total?.before_taxes === 0)
      ? undefined
      : product.total?.before_taxes;

  const fqdn = product.payload.domain?.fqdn && (
    <Fqdn color="base">{product.payload.domain.fqdn}</Fqdn>
  );

  return (
    <ProductItem
      label={label}
      productId={product.id}
      priceBeforeTaxes={price}
      hasTaxes={product.hasTaxes}
      header={
        <>
          <StudioPlan size="advanced" product={product} />

          {fqdn && (langError || product.payload.domain?.lang) && (
            <AlphabetRow
              disabledOption={product.isWorking || isPatchingLang}
              productId={product.id}
              value={product.payload.domain?.lang}
              // $FlowFixMe[incompatible-type]
              onChange={handleLangChange}
            />
          )}
        </>
      }
      errors={filteredProductErrors}
    >
      {fqdn && (
        <ListRow
          icon={<CornerDownRight size={16} />}
          title={fqdn}
          price={<Text color="weak">{__('Included')}</Text>}
        />
      )}
    </ProductItem>
  );
}
